import { useMemo } from 'react';
import type { RoutesContextProps } from '@shared/core';
import { useGetDigitalAndPaperDraftsByEventIdQuery } from '../../apps/card/routes/Dashboard/components/DraftsGallery/DraftsGallery.graphql.generated';
import { useGetStationeryPrintOrdersQuery } from '../../apps/card/serviceCenter/api/queries/getStationeryPrintOrders.graphql.generated';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventUserRole } from '@shared/components/AuthProvider';

interface UseSmartRoutingOptions {
  routeHelpers: RoutesContextProps;
  skip?: boolean;
}

interface SmartRoutingResult {
  saveTheDate: string | null;
  invitation: string | null;
  thankYou: string | null;
  holiday: string | null;
  enclosure: string | null;
  isLoading: boolean;
}

const TYPE_TO_SLUG_MAP: Record<string, string> = {
  [StationeryTemplateCategoryEnum.saveTheDate]: 'save-the-date',
  [StationeryTemplateCategoryEnum.invitation]: 'invitation',
  [StationeryTemplateCategoryEnum.thankYou]: 'thank-you',
  [StationeryTemplateCategoryEnum.holiday]: 'holiday',
  [StationeryTemplateCategoryEnum.enclosure]: 'enclosure',
};

/**
 * Hook that determines the best redirect path for a card type based on user's existing data.
 * Checks for orders first, then drafts, then defaults to browse gallery.
 * Treats missing eventId as a loading state to prevent premature redirects.
 */
export const useSmartRouting = ({ routeHelpers, skip = false }: UseSmartRoutingOptions): SmartRoutingResult => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const { isAdmin, fetching } = useEventUserRole();
  const isAdminStale = !isAdmin || fetching;

  const { data: stationeryDraftsData, loading: draftsLoading } = useGetDigitalAndPaperDraftsByEventIdQuery({
    variables: {
      input: {
        eventId: eventId as string,
      },
      eventId: eventId as string,
    },
    skip: isAdminStale || !eventId || skip,
    batchMode: 'fast',
  });

  const { data: printOrderData, loading: ordersLoading } = useGetStationeryPrintOrdersQuery({
    variables: {
      filters: {
        eventId: eventId as string,
      },
    },
    skip: isAdminStale || !eventId || skip,
    batchMode: 'fast',
  });

  const isLoading = isAdminStale || !eventId || draftsLoading || ordersLoading;

  return useMemo(() => {
    if (skip || isLoading) {
      return {
        saveTheDate: null,
        invitation: null,
        thankYou: null,
        holiday: null,
        enclosure: null,
        isLoading,
      };
    }

    const getPath = (type: StationeryTemplateCategoryEnum): string => {
      const hasOrder = printOrderData?.stationeryPrintOrders.orders.some(order => order.cardDraft?.stationeryTemplate.category === type);
      if (hasOrder) {
        return routeHelpers.buildPath('/cards/orders');
      }

      const hasDraft = stationeryDraftsData?.stationeryDrafts.some(draft => draft?.stationeryTemplate.category === type);
      if (hasDraft) {
        return routeHelpers.buildPath('/cards/drafts');
      }

      return routeHelpers.buildPath(`/cards/wedding/${TYPE_TO_SLUG_MAP[type]}`);
    };

    return {
      saveTheDate: getPath(StationeryTemplateCategoryEnum.saveTheDate),
      invitation: getPath(StationeryTemplateCategoryEnum.invitation),
      thankYou: getPath(StationeryTemplateCategoryEnum.thankYou),
      holiday: getPath(StationeryTemplateCategoryEnum.holiday),
      enclosure: getPath(StationeryTemplateCategoryEnum.enclosure),
      isLoading: false,
    };
  }, [printOrderData, stationeryDraftsData, routeHelpers, skip, isLoading]);
};
